import { useAuth } from '@invara-dev/react-base';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useState } from 'react';
import { InvokeRpc, ServerRpc } from './types';

interface ClientRPC {
  [key: string]: (...args: any[]) => any;
}

type HubReturn<T extends ServerRpc<T>> = [InvokeRpc<T> | undefined, HubConnection | null];

export default function useHookHub<T extends ServerRpc<T>>(
  url: string,
  client: ClientRPC,
  onReconnect?: () => void
): HubReturn<T> {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const auth = useAuth();
  useEffect(() => {
    if (auth.isAuthenticated) {
      return setupConnection(url, client, setConnection, onReconnect);
    }
  }, [auth.isAuthenticated]);

  const invoke = useMemo(() => {
    if (!connection) {
      return undefined;
    }

    return ((methodName, ...args) => connection.invoke(methodName.toString(), ...args)) as InvokeRpc<T>;
  }, [connection]);

  return [invoke, connection];
}

function setupConnection(
  url: string,
  client: ClientRPC,
  callback: (conn: HubConnection) => void,
  onReconnect?: () => void
) {
  const initConnection = new HubConnectionBuilder()
    .withUrl(url)
    .withAutomaticReconnect([0, 2000, 10000, 30000, 60000, 120000])
    .withKeepAliveInterval(1000)
    .withServerTimeout(90000)
    .build();

  //setting up rpc called based on param
  const functions = Object.keys(client);
  for (const procedure of functions) {
    initConnection.on(procedure, (...args) => {
      client[procedure](...args);
    });
  }
  initConnection
    .start()
    .then(() => {
      callback(initConnection);
    })
    .catch((e) => {
      Sentry.captureException(e);
    });

  initConnection.onreconnected(() => {
    if (onReconnect) {
      onReconnect?.();
    }
  });

  return () => {
    initConnection.stop().catch((e) => {
      Sentry.captureException(e);
    });
  };
}
